import { useRef, useState, type FormEvent } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, ShieldAlert, WifiOff } from 'lucide-react';
import { Button, Input, Label, RecordSearchResults, useRecordSearch } from '@higgins/ui';
import { meAtom } from '../store/atoms';
import { searchJobs } from '../lib/api';
import { useOnline } from '../lib/hooks';
import { openJob } from '../lib/jobs';
import type { JobSearchHit } from '../lib/types';
import { ErrorMessage } from '../components/message';
import { RecentJobs } from '../components/recent-jobs';

/** CRM's matches for what was typed; none without signal (the form says why). */
async function findJobs(typed: string): Promise<JobSearchHit[]> {
  return navigator.onLine ? (await searchJobs(typed.trim())).jobs : [];
}

/** What the search has to say about the typed number, under the box. */
function SearchNote({ typed, known, searching, answered, hits }: {
  typed: string;
  known: JobSearchHit | null;
  searching: boolean;
  answered: { term: string; failed: boolean } | null;
  hits: number;
}) {
  if (known) {
    return (
      <p className="mt-2 flex items-center gap-1 text-sm text-slate-700">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
        <span className="font-medium">{known.number}</span>
        <span className="truncate">{known.name ?? 'No job name in CRM'}</span>
      </p>
    );
  }
  if (searching) return <p className="mt-2 text-xs text-slate-500">Searching…</p>;
  if (answered?.term !== typed) return null;
  if (answered.failed) return <p className="mt-2 text-xs text-slate-500">Couldn&rsquo;t search just now. Type the whole job number and press Open.</p>;
  if (hits === 0) return <p className="mt-2 text-xs text-slate-500">No job starting with {typed.trim().toUpperCase()} that you can see in CRM.</p>;
  return null;
}

function OpenJobForm() {
  const online = useOnline();
  const navigate = useNavigate();
  const { query, hits, searching, answered, onQueryChange, fill } = useRecordSearch(findJobs);
  const [picked, setPicked] = useState<JobSearchHit | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLInputElement>(null);
  const number = query.trim().toUpperCase();
  // A picked job, or a match for exactly what was typed: the job exists, so say which it is.
  const known = picked ?? hits.find((hit) => hit.number.toUpperCase() === number) ?? null;

  function type(value: string) {
    setPicked(null);
    setError(null);
    onQueryChange(value);
  }

  function pick(job: JobSearchHit) {
    setPicked(job);
    setError(null);
    fill(job.number);
    // Back to the box, so Return opens the picked job.
    box.current?.focus();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      navigate(`/job/${await openJob(number)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-4">
      <Label htmlFor="job-number">Open a job</Label>
      <div className="mt-2 flex gap-2">
        <Input
          id="job-number"
          ref={box}
          value={query}
          onChange={(e) => type(e.target.value)}
          placeholder="Job number, e.g. SA2007"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          className="flex-1"
        />
        <Button type="submit" disabled={busy || !number}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Open'}
        </Button>
      </div>
      {online ? (
        <SearchNote typed={query} known={known} searching={searching} answered={answered} hits={hits.length} />
      ) : (
        <p className="mt-2 flex items-center gap-1 text-xs text-slate-500">
          <WifiOff className="h-3 w-3 shrink-0" /> No signal, so no job search. Jobs on this device still open, by number or from the list below.
        </p>
      )}
      {online && !picked && hits.length > 0 && (
        <div className="mt-2">
          <RecordSearchResults items={hits} label={(job) => ({ title: job.number, subtitle: job.name })} onPick={pick} />
        </div>
      )}
      {error && <ErrorMessage className="mt-2">{error}</ErrorMessage>}
    </form>
  );
}

export default function HomePage() {
  const me = useAtomValue(meAtom);

  if (!me.entitled) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <ShieldAlert className="mx-auto h-8 w-8 text-amber-500" />
        <h2 className="mt-3 text-base font-semibold text-slate-900">You don&rsquo;t have access yet</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">
          You&rsquo;re signed in as {me.upn}, but you&rsquo;re not in a group entitled to use
          Measure2Quote. Ask the Business Systems team to add you, then reload.
        </p>
      </div>
    );
  }

  return (
    <>
      <OpenJobForm />
      <RecentJobs />
    </>
  );
}
